import * as React from 'react';
import {useTranslation} from 'react-i18next'
import {Checkbox, ListItemText, MenuItem, TextField} from "@mui/material";
import * as Yup from 'yup';
import {addFilter} from "../../../utils/FilterUtils";
import {useSetRecoilState} from 'recoil';
import {STATE_FIXED_VOICE_ACTIVE_FILTERS} from '../constants/ClustersRecoil';
import {
    ID_BE_FREE_EVO,
    ID_CAR_BOX,
    ID_CAR_CLOUD,
    ID_FLEX_RENT,
    ID_TRANSFERS,
    ID_DAMAGE_SHEET
} from "../../mobilitystore/constants/PrintoutTypes";
import AdvancedFilter from "../../../component/AdvancedFilter";
import {
    ID_FILTER_ACRISS,
    ID_FILTER_EVENT_TYPE, ID_FILTER_FROM_KM,
    ID_FILTER_PRINTOUT_TYPE,
    ID_FILTER_SIPP, ID_FILTER_TO_KM,
    ID_FILTER_VALUE_RATE_FROM,
    ID_FILTER_VALUE_RATE_TO
} from "../constants/FixedVoicesFilters";
import {VEHICLE_DELIVERY, VEHICLE_REPLACEMENT} from "../constants/EventTypes";

function FixedVoiceFilter(props: any) {
    const { t } = useTranslation('finance', { keyPrefix: 'clusters' });
    const setFilterDefs = useSetRecoilState(STATE_FIXED_VOICE_ACTIVE_FILTERS);

    const printoutTypes = [
        {
            label: t("printout-types." + ID_BE_FREE_EVO),
            value: ID_BE_FREE_EVO,
            selected: true
        },
        {
            label: t("printout-types." + ID_CAR_CLOUD),
            value: ID_CAR_CLOUD
        },
        {
            label: t("printout-types." + ID_FLEX_RENT),
            value: ID_FLEX_RENT
        },
        {
            label: t("printout-types." + ID_CAR_BOX),
            value: ID_CAR_BOX
        },
        {
            label: t("printout-types." + ID_TRANSFERS),
            value: ID_TRANSFERS
        },
        {
            label: t("printout-types." + ID_DAMAGE_SHEET),
            value: ID_DAMAGE_SHEET
        }
    ];

    const settingsTypes = [
        {
            label: t("settings-types." + VEHICLE_DELIVERY),
            value: VEHICLE_DELIVERY,
            selected: true
        },
        {
            label: t("settings-types." + VEHICLE_REPLACEMENT),
            value: VEHICLE_REPLACEMENT
        }
    ];


    const schema = Yup.object().shape({
        printoutType: Yup.array(),
        settingsType: Yup.array(),
        acriss: Yup.string(),
        sipp: Yup.string(),
        valueRateFrom: Yup.number().transform((value) => Number.isNaN(value) ? null : value)
            .nullable().min(0, t('filters.errors.invalidNumber'))
            .test("valueRateFrom", t('filters.errors.invalidNumber'), (value, context) => {
                return (!value || !context.parent.valueRateTo || value <= context.parent.valueRateTo)
            }),
        valueRateTo: Yup.number().transform((value) => Number.isNaN(value) ? null : value)
            .nullable().min(1, t('filters.errors.invalidNumber'))
            .test("valueRateTo", t('filters.errors.invalidNumber'), (value, context) => {
                return (!value || !context.parent.valueRateFrom || value >= context.parent.valueRateFrom)
            }),
        fromKm: Yup.number().transform((value) => Number.isNaN(value) ? null : value)
            .nullable().min(0, t('filters.errors.invalidNumber'))
            .test("fromKm", t('filters.errors.invalidNumber'), (value, context) => {
                return (!value || !context.parent.toKm || value <= context.parent.toKm)
            }),
        toKm: Yup.number().transform((value) => Number.isNaN(value) ? null : value)
            .nullable().min(1, t('filters.errors.invalidNumber'))
            .test("toKm", t('filters.errors.invalidNumber'), (value, context) => {
                return (!value || !context.parent.fromKm || value >= context.parent.fromKm)
            })
    });

    const defaultValues = {
        printoutType: [],
        settingsType: [],
        acriss: '',
        sipp: '',
        valueRateFrom: '',
        valueRateTo: '',
        fromKm: '',
        toKm: ''
    };

    const renderPrintoutTypeField = (field: any) => (
            <TextField fullWidth
            select
            SelectProps={{
                multiple: true,
                renderValue: (selected: any) =>
                    selected
                        .map((s: any) => printoutTypes.find((ss) => ss.value === s)!.label)
                        .join(", "),
            }}
            label={t("filters.fixed-voices.printout-type")}
            value={field.value}
            onChange={(e) => {
                 field.onChange(e);
             }}
        >
            {printoutTypes.map((status) => (
                <MenuItem key={status.value} value={status.value}>
                    <Checkbox checked={field.value.indexOf(status.value) > -1} />
                    <ListItemText primary={status.label} />
                </MenuItem>
            ))}
        </TextField>
    );


    const renderAcrissField = (field: any) => (
        <TextField fullWidth
                   label={t('filters.fixed-voices.acriss')}
                   value={field.value}
                   onChange={field.onChange}
        />
    );

    const renderSippField = (field: any) => (
        <TextField fullWidth
                   label={t('filters.fixed-voices.sipp')}
                   value={field.value}
                   onChange={field.onChange}
        />
    );

    const renderSettingsTypeField = (field: any) => (
        <TextField fullWidth
                   select
                   SelectProps={{
                       multiple: true,
                       renderValue: (selected: any) =>
                           selected
                               .map((s: any) => settingsTypes.find((ss) => ss.value === s)!.label)
                               .join(", "),
                   }}
                   label={t("filters.fixed-voices.settings-type")}
                   value={field.value}
                   onChange={(e) => {
                       const selectedCount = e.target.value.length;
                       field.onChange(e);
                   }}
        >
            {settingsTypes.map((status) => (
                <MenuItem key={status.value} value={status.value}>
                    <Checkbox checked={field.value.indexOf(status.value) > -1} />
                    <ListItemText primary={status.label} />
                </MenuItem>
            ))}
        </TextField>
    );

    const renderValueRateFromField = (field: any, error: any) => (
        <TextField fullWidth
                   type={"number"}
                   label={t('filters.fixed-voices.value-rate-from')}
                   value={field.value}
                   onChange={field.onChange}
                   error={error !== undefined}
                   helperText={error ? error.message : ""}
        />
    );
    const renderValueRateToField = (field: any, error: any) => (
        <TextField fullWidth
                   type={"number"}
                   label={t('filters.fixed-voices.value-rate-to')}
                   value={field.value}
                   onChange={field.onChange}
                   error={error !== undefined}
                   helperText={error ? error.message : ""}
        />
    );
    const renderFromKmField = (field: any, error: any) => (
        <TextField fullWidth
                   type={"number"}
                   label={t('filters.fixed-voices.from-km')}
                   value={field.value}
                   onChange={field.onChange}
                   error={error !== undefined}
                   helperText={error ? error.message : ""}
        />
    );
    const renderToKmField = (field: any, error: any) => (
        <TextField fullWidth
                   type={"number"}
                   label={t('filters.fixed-voices.to-km')}
                   value={field.value}
                   onChange={field.onChange}
                   error={error !== undefined}
                   helperText={error ? error.message : ""}
        />
    );

    const fields = {
        valueRateFrom: renderValueRateFromField,
        valueRateTo: renderValueRateToField,
        acriss: renderAcrissField,
        sipp: renderSippField,
        fromKm: renderFromKmField,
        toKm: renderToKmField,
    }

    const mainFields ={
        printoutType: renderPrintoutTypeField,
        settingsType: renderSettingsTypeField
    }


    const submit = (data: any) => {
        addFilter(setFilterDefs, ID_FILTER_PRINTOUT_TYPE, data.printoutType);
        addFilter(setFilterDefs, ID_FILTER_ACRISS, data.acriss);
        addFilter(setFilterDefs, ID_FILTER_SIPP, data.sipp);
        addFilter(setFilterDefs, ID_FILTER_EVENT_TYPE, data.settingsType);
        addFilter(setFilterDefs, ID_FILTER_VALUE_RATE_FROM, data.valueRateFrom);
        addFilter(setFilterDefs, ID_FILTER_VALUE_RATE_TO, data.valueRateTo);
        addFilter(setFilterDefs, ID_FILTER_FROM_KM, data.fromKm);
        addFilter(setFilterDefs, ID_FILTER_TO_KM, data.toKm);
    }

    const reset = () => {
        setFilterDefs([]);
    }

    return (
            <AdvancedFilter schema={schema} fields={fields} defaultValues={defaultValues}
                            mainFields={mainFields}
                            onSubmit={submit}
                            onReset={reset}
            />
    )

}

export default FixedVoiceFilter;